import {ReactElement, ReactNode} from 'react';
import {useEffect} from 'react';
import React, {createContext, useContext, useState} from 'react';
import type {DownloadProductsData, Format, ProductsResponse} from '@Libraries/poster-download-library';
import {getDurationForPoster} from '@Libraries/poster-download-library';
import {VideoDesignSizeOptionType} from '@Libraries/poster-download-library';
import {getRecommendedFileTypeFromPosterObject} from '@Libraries/poster-download-library';
import type {PosterLoadObject} from '@PosterWhiteboard/poster/poster.types';
import {FileTypeOptions} from '@Components/download-panel/components/file-type-option/file-type-option.types';
import {DownloadColorProfileOptionType} from '@Components/download-panel/components/download-color-profile-option/download-color-profile-option.types';
import {LetterSizeOptionType} from '@Components/download-panel/components/download-adjust-letter-size-option/download-adjust-letter-size-option.types';
import {useAppSelector} from '@/hooks';
import {DownloadBleedOptionTypes} from '@Components/download-panel/components/download-bleed-option/download-bleed-option.types';

interface DownloadPanelContextData {
  productsData?: DownloadProductsData[];
  selectedProductId: string;
  posterObject: PosterLoadObject;
  availableFormats?: Record<string, Format[]>;

  scale: number;
  setSelectedProductId?: (newVal: string) => void;
  setScale: (newVal: number) => void;
  transparency: boolean;
  setTransparency: (newVal: boolean) => void;
  margin: boolean;
  setMargin: (newVal: boolean) => void;
  colorProfile: DownloadColorProfileOptionType;
  setColorProfile: (newVal: DownloadColorProfileOptionType) => void;
  letterSize: LetterSizeOptionType;
  setLetterSize: (newVal: LetterSizeOptionType) => void;
  bleedUnit: DownloadBleedOptionTypes;
  setBleedUnit: (newVal: DownloadBleedOptionTypes) => void;
  bleed: number;
  setBleed: (newVal: number) => void;
  speed: number;
  setSpeed: (newVal: number) => void;
  duration: number;
  setDuration: (newVal: number) => void;
  fileType: FileTypeOptions;
  setFileType: (newVal: FileTypeOptions) => void;
  isWebDownload: boolean;
  videoScale: VideoDesignSizeOptionType;
  setVideoScale: (newVal: VideoDesignSizeOptionType) => void;
  videoSliderScale: number;
  setVideoSliderScale: (newVal: number) => void;
  originalDuration: number;
  isPDFWebQuality: boolean;
  setIsPDFWebQuality: (newVal: boolean) => void;
}

const DownloadPanelContext = createContext<DownloadPanelContextData | null>(null);

export function useDownloadPanelData(): DownloadPanelContextData {
  const context = useContext(DownloadPanelContext);
  if (context === null) {
    throw new Error('useDownloadPanelData must be used within a ProductsDataProvider');
  }
  return context;
}

interface DownloadPanelProviderProps {
  children: ReactNode;
  productsResponse?: ProductsResponse;
  posterObject: PosterLoadObject;
}

const DEFAULT_SCALE = 1;
const DEFAULT_BLEED = 0;
const DEFAULT_SPEED = 1;

export function DownloadPanelProvider({children, ...props}: DownloadPanelProviderProps): ReactElement {
  const [scale, setScale] = useState<number>(DEFAULT_SCALE);
  const [bleed, setBleed] = useState<number>(DEFAULT_BLEED);
  const [bleedUnit, setBleedUnit] = useState<DownloadBleedOptionTypes>(DownloadBleedOptionTypes.INCHES);
  const [colorProfile, setColorProfile] = useState<DownloadColorProfileOptionType>(DownloadColorProfileOptionType.RGB);
  const [transparency, setTransparency] = useState(false);
  const [margin, setMargin] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<string>(FileTypeOptions.JPG);
  const [speed, setSpeed] = useState<number>(DEFAULT_SPEED);
  const [letterSize, setLetterSize] = useState<LetterSizeOptionType>(LetterSizeOptionType.NO);
  const [recommendedFileType, setRecommendedFileType] = useState<FileTypeOptions>(getRecommendedFileTypeFromPosterObject(props.posterObject));
  const [fileType, setFileType] = useState<FileTypeOptions>(getRecommendedFileTypeFromPosterObject(props.posterObject));
  const [videoScale, setVideoScale] = useState(VideoDesignSizeOptionType.FULL_HD);
  const posterTypeNames = useAppSelector((state) => {
    return state.downloadDialog.webDownloadPosterTypeNames;
  });
  const [posterObjectCopy, setPosterObjectCopy] = useState<PosterLoadObject>(props.posterObject);
  const [isWebDownload, setIsWebDownload] = useState(false);
  const [videoSliderScale, setVideoSliderScale] = useState(2);
  const [duration, setDuration] = useState<number>(getDurationForPoster(props.posterObject));
  const [originalDuration, setOriginalDuration] = useState<number>(getDurationForPoster(props.posterObject));
  const [isPDFWebQuality, setIsPDFWebQuality] = useState(true);

  useEffect(() => {
    setPosterObjectCopy(props.posterObject);
    setOriginalDuration(getDurationForPoster(props.posterObject));

    const newRecommended = getRecommendedFileTypeFromPosterObject(props.posterObject);
    if (newRecommended !== recommendedFileType) {
      setFileType(newRecommended);
      setRecommendedFileType(newRecommended);
    }
  }, [props.posterObject]);

  useEffect(() => {
    if (posterTypeNames && posterTypeNames.length > 0) {
      const isWeb = posterTypeNames.includes(props.posterObject.type.name);
      setIsWebDownload(isWeb);
    }
  }, [props.posterObject.type.name, posterTypeNames]);

  const value: DownloadPanelContextData = {
    productsData: props.productsResponse?.productsData,
    selectedProductId: selectedProductId,
    setSelectedProductId,
    posterObject: posterObjectCopy,
    scale,
    transparency,
    setTransparency,
    setScale,
    margin,
    setMargin,
    bleed,
    setBleed,
    colorProfile,
    setColorProfile,
    speed,
    setSpeed,
    letterSize,
    setLetterSize,
    fileType,
    setFileType,
    isWebDownload,
    bleedUnit,
    setBleedUnit,
    duration,
    setDuration,
    videoScale,
    setVideoScale,
    videoSliderScale,
    setVideoSliderScale,
    originalDuration,
    isPDFWebQuality,
    setIsPDFWebQuality,
  };

  return <DownloadPanelContext.Provider value={value}>{children}</DownloadPanelContext.Provider>;
}
